/* eslint-disable camelcase */
import get from 'lodash.get';

import striptags from 'striptags';
import copy from '../../config/copy';
import reviewSet from './reviewSet';
import utils from './index';
import { getBlankImage, getImageUrl } from '../cloudinary';
import { generateAffiliatesUrl } from './domainHelpers';
import trackonomicsLinkBuilder from 'utils/trackonomicsLinkBuilder';

export const getDocSiteKey = (item, siteKey) => {
  // there are various paths to site list depending on api the endpoint
  // and document type.
  const siteList =
    item?.siteList ??
    item?.metaData?.siteList ??
    item?.metaData?.fields?.siteList;

  // If host brand is not in document's site list, select first non-blank
  // brand key from siteList
  // Documents occasionally have blank brand keys in their site lists,
  // so filter this out.
  // ex: siteList: ["", "cco"]
  if (siteList && !siteList?.includes(siteKey))
    return siteList.filter((brandKey) => brandKey.length > 0)[0].toLowerCase();

  return siteKey;
};

const generateAttribution = (item, siteKey, showComments = true) => {
  const { resourceType, resourceTypeFormatted, season, show, number } = item;
  const siteName = utils.siteKeySiteNameMap[siteKey];
  let val = '';
  if (resourceType === 'video' && siteKey !== 'cio') {
    let showSeason = '';
    let showName = '';
    if (season) showSeason = ` from Season ${season}`;
    if (siteName) showName = ` • ${siteName}`;
    val = `Clip${showSeason}${showName}`;
  } else if (resourceType === 'guide') {
    val = 'Guide';
  } else if (showComments) {
    val = resourceTypeFormatted;
  } else if (resourceType === 'episode') {
    let formattedNumber = number?.toString();
    if (formattedNumber?.length > 2) {
      // Some episode numbers are including the season they're in. Check if the
      // episode number includes season information, if so, remove it from the
      // number.
      const lengthDiff = formattedNumber.length - 2;
      formattedNumber = formattedNumber.substring(lengthDiff);
    }
    const showName = show?.title || siteName;
    val = `${showName} • S${season} E${formattedNumber}`;
  } else if (resourceType.includes('::') || resourceType.includes('_')) {
    val = utils.docTypeFormatted[resourceType];
  } else {
    val = `${resourceTypeFormatted}${siteName ? ` • ${siteName}` : ''}`;
  }

  return val;
};

/**
 * A user is authenticated if their user object exists, and has one or more
 * active registrations.
 *
 * @param {UserState} user
 * @returns {boolean}
 */
const getIsAuthenticated = (user) => {
  return user?.segment !== 'anonymous';
};

/**
 * If a user is authenticated and a recipe has rating data, then display recipe
 * attributions.
 *
 * @param {boolean} isAuthenticated
 * @param {number} avgRating
 * @param {number} numRating
 * @returns {boolean}
 */
const getDisplayRecipeAttributions = (isAuthenticated, avgRating, numRating) =>
  isAuthenticated && avgRating && numRating;

const defaultCardOptions = {
  isAuthenticated: false,
  siteKey: 'atk',
  subdomain: 'www',
  includeCta: false,
  includeDescription: true,
  includeSubtitle: true,
};

export const documentToGenericCard = (doc, options = {}) => {
  const {
    includeFavorites = false,
    isAuthenticated,
    siteKey,
    subdomain,
  } = {
    ...defaultCardOptions,
    ...options,
  };

  const {
    metaData: {
      fields: { photo },
    },
    documentType,
    subtitle,
    slug,
    title,
  } = doc;

  const cardItem = {
    id: doc.id,
    fields: {
      photo,
      siteKey,
      subtitle: subtitle || documentType,
      title,
      url: `/${documentType}s/${slug}`,
    },
    componentType: 'cmsComponentGenericCard',
  };

  if (includeFavorites) {
    const { favoritableId, favoritableSiteKey } =
      utils.getFavoritableDataFromDocument(
        doc,
        siteKey,
        subdomain,
        isAuthenticated,
      );

    cardItem.fields.siteKey = favoritableSiteKey.toLowerCase();
    cardItem.fields.favoritableId = favoritableId;
  }

  return cardItem;
};

const getProductData = (item, queryParams, siteKey) => {
  let productData = {};
  const productReference =
    item.productReference || get(item, 'fields.productReference.fields');
  if (productReference) {
    productData = reviewSet.getProductData(productReference, {
      displayPrice: false,
      queryParams,
      siteKey,
    });
  }
  return productData;
};

const defaultDBCOptions = {
  affiliateCodeTag: '-equippilot-20',
  includeCta: false,
  includeDescription: true,
  includeFavorite: true,
  includeSubtitle: true,
  isAuthenticated: false,
  photoConfig: {},
  queryParams: null,
  siteKey: 'atk',
  subdomain: 'www',
};

export const documentToDocumentBrowseCard = (item, options = {}) => {
  if (!item) return {};
  // empty values to start
  let categoryLabels;
  let label;
  let photo;
  let video;

  // combine defaults and passed in options for item config
  const {
    affiliateCodeTag,
    imageBgColor = 'fcf9f3',
    includeDescription,
    includeFavorite,
    includeSubtitle,
    isAuthenticated,
    photoConfig,
    queryParams,
    siteKey,
    subdomain,
  } = {
    ...defaultDBCOptions,
    ...options,
  };

  // itemType can vary based on doppio/latte parsing
  let itemType =
    item.documentType ||
    get(item, 'fields.documentType', item.componentType) ||
    item.contentType ||
    item.docType;

  // affiliate code depends on isReviewable
  const isReviewable = ['equipment_review', 'taste_test'].includes(itemType);
  // grids can contain documents or wrapper components called `editorialCollectionItemOverride`
  // the wrappers have a `document` and several override fields (title, description, etc)
  const hasOverrides = itemType === 'editorialCollectionItemOverride';
  // choose an object based on whether or not an override is present
  const documentItem = hasOverrides ? item.fields.document : item;
  if (hasOverrides)
    itemType = get(item, 'fields.document.fields.documentType', 'recipe');

  if (!documentItem) return {};

  // favoritable id can vary based on isAuthenticated, siteKey and document siteKeys
  const { favoritableId, favoritableSiteKey } =
    utils.getFavoritableDataFromDocument(
      documentItem,
      siteKey,
      subdomain,
      isAuthenticated,
    );

  const affiliateCode = isReviewable
    ? `${favoritableSiteKey}${affiliateCodeTag}`
    : undefined;
  const { ctaTitle, ctaUrl } = getProductData(
    documentItem,
    queryParams,
    siteKey,
  );

  // start with the attributes directly on the document
  let { description, id, metaData, searchStickers, slug, subtitle, title } =
    documentItem;

  // account for doppio vs latte data structure differences
  // override all existing variables with the same name
  if (get(documentItem, 'fields.slug', null)) {
    ({
      description,
      id,
      metaData,
      searchStickers,
      slug,
      subtitle,
      title,
      video,
    } = documentItem.fields);
  }

  // if overrides are present, use each one if it has a value
  if (hasOverrides) {
    const {
      shortDescription: overrideDescription,
      label: overrideLabel,
      photo: overridePhoto,
      title: overrideTitle,
    } = item.fields;
    label = overrideLabel;
    if (overrideDescription && overrideDescription.length)
      description = overrideDescription;
    if (overridePhoto && Object.keys(overridePhoto).length)
      photo = overridePhoto;
    if (overrideTitle && overrideTitle.length) title = overrideTitle;
  }

  // subtitle is rarely available - default is humanized itemType
  // i.e. buying_guide = Buying Guide, etc
  if (!subtitle) {
    subtitle = copy.documentTypes[itemType] || '';
  }

  if (itemType === 'episode') {
    subtitle = copy.relatedEpisodeTitle;
  }

  if (itemType === 'article') {
    subtitle = copy.relatedArticleTitle;
  }

  // video is defined for docs with zype data - use the zype thumb
  if (video) photo = { public_id: utils.getZypeThumbnail('sm', video) };

  if (metaData) {
    // account for doppio vs latte data structure differences
    if (!photo) photo = get(metaData, 'fields.photo', metaData.photo);
    // reviewset landing page document collections
    if (!photo && metaData.meta_image)
      photo = { public_id: metaData.meta_image };
    if (!description) description = get(metaData, 'fields.description');
    categoryLabels = get(
      metaData,
      'fields.categoryLabels.values',
      metaData.categoryLabels,
    );
    // editors include shorter titles for collections
    if (itemType === 'collection') {
      title = get(metaData, 'fields.title', title);
    }
  }

  if (photo) {
    photo.config = photoConfig;
    if (favoritableSiteKey === 'cio' && imageBgColor) {
      photo.config.effect = `replace_color:${imageBgColor}:100:ffffff`;
    }
  }

  let docSlug = slug;
  if (id && itemType !== 'collection') {
    if (!slug?.startsWith(id)) docSlug = `${id}-${slug}`;
  }
  const url = utils.getDocumentUrl(
    itemType,
    docSlug,
    favoritableSiteKey,
    subdomain,
  );

  return {
    affiliateCode,
    categoryLabels,
    ctaTitle,
    ctaUrl,
    description,
    documentType: itemType,
    includeCta: isReviewable,
    includeDescription,
    includeFavorite,
    includeSubtitle,
    favoritableId: favoritableId || options.favoritableId,
    label,
    searchStickers,
    siteKey: favoritableSiteKey,
    title,
    subtitle,
    photo,
    url,
  };
};

function userAttributionsFromDocument(document = {}) {
  const { fields: { commentsCount: searchComments, rating } = {} } = document;

  return {
    avgRating: rating?.attributes?.avgScore,
    numRatings: rating?.attributes?.userRatingsCount,
    searchComments,
  };
}

const documentToFeatureCard = (item, user, domainSiteKey, subdomain, tag) => {
  const isAuthenticated = getIsAuthenticated(user);
  const siteKey = getDocSiteKey(item, domainSiteKey);
  const card = documentToDocumentBrowseCard(item, {
    affiliateCodeTag: tag,
    isAuthenticated,
    siteKey,
    subdomain,
    includeFavorite: true,
  });
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    card.url,
    card.siteKey,
  );
  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);
  const cloudinaryId =
    card?.photo?.public_id || item.landscapeHeroPhoto?.publicId;
  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'thumbnail', { aspectRatio: '1:1' })
    : getBlankImage();

  const dataAttrs = {
    'data-title': card.title,
    'data-type': card.documentType,
    'data-link': card.url,
  };

  let ctaDataAttrs = {};
  if (item.winner) {
    const { title: review_set } = item;
    const { affiliates, price, recommendationStatus, name, asin } = item.winner;
    const retailer = affiliates[siteKey].name;
    ctaDataAttrs = {
      'data-asin': asin,
      'data-recommendation-status': recommendationStatus,
      'data-price': price,
      'data-retailer': retailer,
      'data-reviewable': name,
      'data-type': card.documentType,
      'data-review-set': review_set,
      'data-affiliate-tag': 'atkfeaturedcard-20',
    };
  }

  const { avgRating, numRatings, searchComments } =
    userAttributionsFromDocument(item?.fields?.document);
  const displayRecipeAttribution = getDisplayRecipeAttributions(
    isAuthenticated,
    avgRating,
    numRatings,
  );

  const showComments = searchComments && searchComments > 0;
  const contentTypeFormatted =
    card.documentType.includes('::') ||
    card.documentType.includes('_') ||
    card.documentType === 'productcollection'
      ? utils.docTypeFormatted[card.documentType]
      : card.documentType;
  const final = {
    attributions: generateAttribution(
      {
        resourceType: card.documentType ?? item?.fields?.document?.documentType,
        resourceTypeFormatted:
          card?.subtitle && card.subtitle.length > 0
            ? card.subtitle
            : item?.resourceTypeFormatted,
        show: item.show,
      },
      card.siteKey,
      showComments,
    ),
    avgRating,
    commentsCount: item.search_comment_count || item.commentsCount,
    contentType: card.documentType ?? item?.document?.docType,
    contentTypeFormatted,
    ctaDataAttrs,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton,
    displayRecipeAttribution,
    displaySecondaryAttribution: true,
    favoriteRibbonColor: utils.siteKeyToRibbonColor(domainSiteKey),
    href: card.url,
    objectId: favoritableId || `${card.documentType}_${item.id}`,
    imageUrl,
    numRatings,
    searchAttribution: true,
    searchComments: showComments ? searchComments : null,
    siteKey: card.siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers: utils.getStickers(item.publishDate, card.searchStickers),
    target: card.siteKey !== domainSiteKey ? '_blank' : null,
    title: utils.truncateString(card.title, 59),
  };
  return final;
};

const trendingToDocumentBrowseCard = (item, currentSiteKey, subdomain) => {
  const {
    cloudinary_id: cloudinaryId,
    document_type: contentType,
    document_id: documentId,
    title,
    site_keys: siteKeys,
  } = item;

  const siteKey = siteKeys.includes(currentSiteKey)
    ? currentSiteKey
    : siteKeys[0];

  const url = utils.getDocumentUrl(contentType, documentId, siteKey, subdomain);

  const imageUrl = getImageUrl(cloudinaryId, 'thumbnail', {
    aspectRatio: '1:1',
  });

  const transformedItem = {
    contentType,
    displaySecondaryAttribution: true,
    includeCta: false,
    imageUrl,
    secondaryAttribution: utils.siteKeySiteNameMap[siteKey],
    siteKey,
    title,
    url,
  };
  return transformedItem;
};

export const algoliaHitToDocumentBrowseCard = (item, options = {}) => {
  const {
    currentSiteKey,
    description,
    imageBgColor,
    includeDescription,
    includeFavorite,
    includeSubtitle,
    objectID,
    photoConfig = {},
    search_cloudinary_id: cloudinaryId,
    search_document_klass: documentType,
    search_price: previousPrice,
    search_related_data: relatedData,
    search_sale_price: currentPrice,
    search_site_list: validSiteKeys,
    search_url: searchUrl,
    subdomain = 'www',
    title,
  } = {
    ...item,
    ...options,
  };

  const documentTypeFormatted =
    utils.docTypeFormatted[documentType?.toLowerCase()] ?? documentType ?? '';

  const isBook = documentTypeFormatted.toLowerCase().includes('book');
  const currentPriceInPennies = currentPrice
    ? parseFloat(currentPrice.replace('$', ''), 10) * 100
    : null;
  const previousPriceInPennies = previousPrice
    ? parseFloat(previousPrice.replace('$', ''), 10) * 100
    : null;

  let siteKey = validSiteKeys.includes(currentSiteKey)
    ? currentSiteKey
    : validSiteKeys[0];

  const photo = cloudinaryId ? { id: cloudinaryId } : null;
  if (photo) {
    photo.config = photoConfig;
    if (imageBgColor) {
      photo.config.effect = `replace_color:${imageBgColor}:100:ffffff`;
    }
    if (isBook) {
      photo.config.aspectRatio = null;
    }
  }

  let url;
  let subtitle = documentTypeFormatted;
  let ctaTitle;
  let ctaUrl;
  if (isBook) {
    ctaTitle = 'Buy Cookbook';
    subtitle = 'Shop';
    // NOTE - thiese sourceKey values are specific to the quick view
    // If we want to have more keys in the future we may have to add
    // a sourceKeyContext value param
    const sourceKey = {
      atk: 'CAAD1BKQ0',
      cco: 'CKAD1BKQ0',
      cio: 'CCAD1BKQ0',
    }[currentSiteKey];
    if (documentType === 'book' && relatedData) {
      let parsedData;
      try {
        parsedData = JSON.parse(relatedData);
        url = sourceKey
          ? `${parsedData.url.replace(
              /sourcekey=(.*)/,
              `sourcekey=${sourceKey}`,
            )}`
          : parsedData.url;
      } catch (err) {
        url = searchUrl;
        // eslint-disable-next-line no-console
        console.log('error parsing related data for ', objectID); // eslint-disable-line
      }
      siteKey = 'shop';
    } else {
      url = `${searchUrl}${sourceKey ? `?sourcekey=${sourceKey}` : ''}`;
    }
    ctaUrl = url;
  } else {
    /* search urls are formatted as: '/docType/slug' so searchUrl.split('/')[2]
     * returns the slug
     */
    url = utils.getDocumentUrl(
      documentType,
      searchUrl.split('/')[2],
      siteKey,
      subdomain,
    );
  }

  const includeCta = ctaTitle && ctaUrl;

  // CV articles don't have favorite buttons and aren't in `document_summaries` table
  // which makes it impossible to favorite them
  const noFav = objectID.startsWith('article_cv_') || isBook;

  const favoritableId = noFav ? null : objectID;

  return {
    ctaTitle,
    ctaUrl,
    currentPriceInPennies,
    description,
    documentType,
    documentTypeFormatted,
    includeDescription,
    includeFavorite,
    includeSubtitle,
    favoritableId,
    includeCta,
    previousPriceInPennies,
    siteKey,
    subtitle,
    title,
    photo,
    url,
  };
};

export const algoliaHitToFeatureCard = (item, options, isAuthenticated) => {
  const card = algoliaHitToDocumentBrowseCard(item, options);
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    card.url,
    card.siteKey,
  );

  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);

  const {
    search_document_date: searchDocDate,
    search_stickers: searchStickers,
    search_related_data: rel,
    search_cloudinary_id: cloudinaryId,
    [`search_${favoritableSiteKey}_buy_now_link`]: ctaUrl,
  } = item;

  let siteDocDate;
  if (rel && rel.includes && rel?.includes('search_published_date')) {
    try {
      const { sites } = JSON.parse(rel);
      siteDocDate = sites?.[favoritableSiteKey]?.search_published_date; // eslint-disable-line camelcase, max-len
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('bad related data');
    }
  }

  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'documentCard', {
        aspectRatio: '17:25',
        background: null,
        height: 400,
      })
    : getBlankImage();

  const dataAttrs = {
    'data-title': card.title,
    'data-type': card.documentType,
    'data-link': card.url,
  };

  let url = ctaUrl;
  if (ctaUrl && ctaUrl.includes('amazon.com')) {
    url = ctaUrl.split('?tag')[0];
    url = `${url}?tag=atkfeaturedcard-20`;
  }

  return {
    attributions: generateAttribution(
      {
        resourceType: card.documentType,
        resourceTypeFormatted: card.documentTypeFormatted,
      },
      card.siteKey,
    ),
    commentsCount: item.search_comment_count || item.commentsCount,
    contentType: card.documentTypeFormatted,
    ctaText: 'Buy the Winner',
    ctaUrl: url,
    imageAlt: card.title,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton,
    displaySecondaryAttribution: true,
    href: card.url,
    lazyImage: false,
    objectId: favoritableId,
    imageUrl,
    siteKey: card.siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers: utils.getStickers(siteDocDate || searchDocDate, searchStickers),
    title: utils.truncateString(card.title, 59),
  };
};

const defaultGenericCardOptions = {
  includeDescription: true,
  includeSubtitle: true,
  photoConfig: {},
  siteKey: 'atk',
  subdomain: 'www',
};

export const genericCardToDocumentBrowseCard = (item, options = {}) => {
  if (!item) return {};

  // combine defaults and passed in options for item config
  const {
    imageBgColor = 'fcf9f3',
    includeDescription,
    includeSubtitle,
    photoConfig,
    siteKey,
  } = {
    ...defaultGenericCardOptions,
    ...options,
  };

  const {
    fields: {
      affiliateCode,
      buyNowUrl,
      description,
      newPrice,
      oldPrice,
      photo,
      subtitle,
      title,
      url,
    },
  } = item;

  let cardSiteKey = siteKey;
  if (url?.includes('shop.americastestkitchen')) cardSiteKey = 'shop';
  const currentPriceInPennies = newPrice
    ? parseFloat(newPrice.replace('$', ''), 10) * 100
    : null;
  const previousPriceInPennies = oldPrice
    ? parseFloat(oldPrice.replace('$', ''), 10) * 100
    : null;

  if (photo) {
    photo.config = photoConfig;
    if (cardSiteKey === 'cio' && imageBgColor) {
      photo.config.effect = `replace_color:${imageBgColor}:100:ffffff`;
    }
    photo.aspect_ratio = '1:1';
  }
  return {
    affiliateCode,
    buyNowUrl,
    currentPriceInPennies,
    description,
    includeDescription,
    includeSubtitle,
    photo,
    previousPriceInPennies,
    siteKey: cardSiteKey,
    subtitle,
    title,
    url,
  };
};

const trendingToStandardCard = (item, user, domainSiteKey, subdomain) => {
  const {
    cloudinary_id: cloudinaryId,
    document_type: contentType,
    title,
    slug,
    site_keys: siteKeys,
  } = item;

  const isAuthenticated = getIsAuthenticated(user);
  const siteKey = siteKeys.includes(domainSiteKey)
    ? domainSiteKey
    : siteKeys[0];

  const href = utils.getDocumentUrl(contentType, slug, siteKey, subdomain);

  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );

  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);
  const imageUrl = getImageUrl(cloudinaryId, 'thumbnail', {
    aspectRatio: '1:1',
  });
  const siteName = utils.siteKeySiteNameMap[siteKey];

  const transformedItem = {
    contentType,
    contentTypeFormatted: contentType,
    displayLockIcon: false,
    displayFavoritesButton,
    displaySecondaryAttribution: true,
    href,
    imageUrl,
    objectId: favoritableId,
    secondaryAttribution: siteName,
    siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers: [],
    title: utils.truncateString(title, 52),
  };
  return transformedItem;
};

const itemToFeatureCard = (
  item,
  userData = {},
  domainSiteKey,
  subdomain = 'www',
) => {
  const isAuthenticated = getIsAuthenticated(userData);
  const siteKey = utils.getDocSiteKey(item, domainSiteKey);

  const {
    isWide = false,
    metaData,
    resourceType,
    publishDate,
    searchStickers,
    slug,
    title,
  } = item;

  const href = utils.getDocumentUrl(resourceType, slug, siteKey, subdomain);
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );
  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);
  const cloudinaryId =
    metaData?.verticalPhoto?.publicId ||
    metaData?.photo?.publicId ||
    metaData?.photo?.public_id;
  const aspectRatio = isWide ? '7:5' : '17:25';
  const dataAttrs = {
    'data-title': title,
    'data-type': resourceType,
    'data-id': slug,
    'data-link': href,
  };
  return {
    attributions: generateAttribution(item, siteKey),
    commentsCount: item.search_comment_count || item.commentsCount,
    contentType: resourceType,
    displayLockIcon: false,
    dataAttrs,
    displayFavoritesButton,
    displaySecondaryAttribution: true,
    href,
    objectId: favoritableId,
    imageUrl: getImageUrl(cloudinaryId, 'documentCard', {
      aspectRatio,
      height: 400,
      width: 272,
    }),
    siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers: utils.getStickers(publishDate, searchStickers),
    title,
  };
};

const relatedToFeatureCard = ({
  item,
  userData = {},
  domainSiteKey,
  subdomain,
}) => {
  const isAuthenticated = getIsAuthenticated(userData);
  const siteKey = utils.getDocSiteKey(item, domainSiteKey);

  const {
    isWide = false,
    metaData,
    resourceType,
    searchStickers,
    slug,
    title,
    avgRating,
    numRatings,
  } = item;
  let stickers;
  if (searchStickers?.length === 1) {
    stickers = [{ text: searchStickers[0], type: 'editorial' }];
  }
  const { ctaUrl } = reviewSet.getDocData(item, { siteKey });
  let url = ctaUrl;
  if (ctaUrl && ctaUrl?.includes('amazon.com')) {
    url = ctaUrl.split('?tag')[0];
    url = `${url}?tag=atkfeaturedcard-20`;
  }
  const href = utils.getDocumentUrl(resourceType, slug, siteKey, subdomain);
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );

  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);
  const cloudinaryId =
    metaData?.verticalPhoto?.publicId ||
    metaData?.photo?.publicId ||
    metaData?.photo?.public_id;
  const aspectRatio = isWide ? '7:5' : '17:25';
  const dataAttrs = {
    'data-title': title,
    'data-type': resourceType,
    'data-id': slug,
    'data-link': href,
  };
  return {
    attributions: generateAttribution(item, siteKey),
    commentsCount: item.search_comment_count || item.commentsCount,
    contentType: resourceType,
    ctaText: 'Buy the Winner',
    ctaUrl: url,
    imageAlt: title,
    displayLockIcon: false,
    dataAttrs,
    displayFavoritesButton,
    displaySecondaryAttribution: true,
    href,
    objectId: favoritableId,
    imageUrl: getImageUrl(cloudinaryId, 'documentCard', {
      aspectRatio,
      height: 400,
      width: 272,
    }),
    siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers,
    title,
    avgRating: isAuthenticated ? avgRating : null,
    numRatings: isAuthenticated ? numRatings : null,
  };
};

const itemToPersonCard = (item) => {
  const {
    bio: description,
    firstName,
    lastName,
    photo: { publicId: imgCloudinaryId } = {},
    mode,
  } = item;
  return {
    description,
    imgAlt: ' ',
    imgCloudinaryId,
    name: `${firstName} ${lastName}`,
    mode,
  };
};

const itemToStandardCard = (
  item,
  user,
  domainSiteKey,
  subdomain = 'www',
  tag,
) => {
  const {
    commentsCount,
    metaData,
    price,
    publishDate,
    rating,
    searchStickers,
    salePrice,
    sourceKey,
    slug,
    title,
    resourceType: contentType,
    resourceTypeFormatted: contentTypeFormatted,
    winner,
    url = '',
  } = item;
  const isAuthenticated = getIsAuthenticated(user);
  let avgScore, numRatings;
  if (isAuthenticated && rating) {
    avgScore = rating?.attributes?.avgScore;
    numRatings = rating?.attributes?.userRatingsCount;
  }
  const siteKey = utils.getDocSiteKey(item, domainSiteKey);
  let href = utils.getDocumentUrl(contentType, slug, siteKey, subdomain);
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );
  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);

  const cloudinaryId = metaData?.photo?.publicId || metaData?.photo?.public_id;
  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'thumbnail', { aspectRatio: '1:1' })
    : null;
  const stickers = utils.getStickers(publishDate, searchStickers);
  const shopSalePrice = salePrice === price ? null : salePrice;
  const shopPrices = price ? { price, salePrice: shopSalePrice } : null;

  let displaySecondaryAttribution = true;
  let ctaText = null;
  let ctaUrl = null;
  let ctaDataAttrs = null;
  let target = siteKey !== domainSiteKey ? '_blank' : null;

  if (
    contentType &&
    (contentType.toLowerCase().includes('equipment') ||
      contentType.toLowerCase().includes('taste'))
  ) {
    if (winner) {
      const { name, recommendationStatus } = winner;
      const winnerData = reviewSet.getDocData(item, { siteKey, tag });
      const { asin, price } = winnerData;
      if (asin) {
        const url = winnerData.ctaUrl.split('?tag')[0];
        const affiliateTag = 'tag=atkstandardcard-20';
        ctaUrl = `${url}?${affiliateTag}`;
        ctaText = 'Buy the Winner';

        ctaDataAttrs = {
          'data-asin': asin,
          'data-price': price,
          'data-reviewable': name,
          'data-review-set': title,
          'data-affiliate-tag': affiliateTag,
          'data-affiliate-url': ctaUrl,
          'data-recommendation-status': recommendationStatus,
          'data-retailer': 'Amazon',
        };
      }
    }
  } else if (contentType === 'RetailProduct') {
    const hasKey = url.includes(sourceKey);
    const joiner = url.includes('?') ? '&' : '?';
    ctaUrl = `${url}${
      sourceKey && !hasKey ? `${joiner}sourcekey=${sourceKey}` : ''
    }`;
    ctaText =
      price && shopSalePrice
        ? `Buy Now & Save ${Math.abs(
            Math.round(Math.min(salePrice / price) * 100) - 100,
          )}%`
        : 'Buy Now';
    displaySecondaryAttribution = false;
    href = ctaUrl;
    target = '_blank';
  } else if (contentType === 'ProductCollection') {
    displaySecondaryAttribution = false;
  }

  const dataAttrs = {
    'data-title': title,
    'data-type':
      contentType === 'Cms::Page::Article'
        ? 'article'
        : contentType?.toLowerCase(),
    'data-id': slug,
    'data-link': href,
  };

  return {
    avgRating: avgScore,
    contentType,
    contentTypeFormatted,
    ctaDataAttrs,
    ctaText,
    ctaUrl,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton,
    displaySecondaryAttribution,
    displayRecipeAttribution: true,
    href,
    imageUrl,
    numRatings,
    objectId: favoritableId,
    searchAttribution: true,
    searchComments: commentsCount,
    shopPrices,
    siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers,
    target,
    title: utils.truncateString(title, 59),
  };
};

/**
 * Asin needed for ctaAttr for buy now click event. Parse from url like
 *  browse seems to do currently.
 * @param {string | undefined} ctaUrl
 * @returns {string | undefined}
 */
function tryAsinFromCtaUrl(ctaUrl) {
  const matches = /www\.amazon\.com\/dp\/([a-zA-Z0-9]+)/.exec(ctaUrl);
  return matches?.[1];
}

const algoliaHitToStandardCard = (
  item,
  user,
  domainSiteKey,
  subdomain = 'www',
  tag,
) => {
  const {
    search_avg_rating: avgRating,
    search_user_ratings_count: numRatings,
    search_atk_buy_now_link: atkBuyNowLink,
    search_cio_buy_now_link: cioBuyNowLink,
    search_cco_buy_now_link: ccoBuyNowLink,
    search_cloudinary_id: cloudinaryId,
    search_comment_count: commentsCount,
    search_document_klass: contentType,
    search_document_klass_formatted: contentTypeFormatted = '',
    search_price: price,
    search_published_date: publishDate,
    search_sale_price: salePrice,
    search_site_list: siteList = ['atk'],
    search_stickers: searchStickers,
    search_url: slug,
    title,
  } = item;
  const buyNowLinks = {
    atk: atkBuyNowLink,
    cio: cioBuyNowLink,
    cco: ccoBuyNowLink,
  };
  const isAuthenticated = getIsAuthenticated(user);

  const siteKey = siteList.includes(domainSiteKey)
    ? domainSiteKey
    : siteList[0];
  const href = utils.getDocumentUrl(contentType, slug, siteKey, subdomain);
  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );
  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);

  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'thumbnail', { aspectRatio: '1:1' })
    : null;
  const publishDateString = publishDate.toString();
  const documentDate = `${publishDateString.substr(
    0,
    4,
  )}/${publishDateString.substr(4, 2)}/${publishDateString.substr(6, 2)}`;
  const stickers = utils.getStickers(documentDate, searchStickers);
  // add recipe ratings in to this later
  const searchAttribution = commentsCount || null;
  const displaySecondaryAttribution = true;
  let ctaText = null;
  let ctaUrl = null;
  let ctaDataAttrs = null;
  const target = siteKey !== domainSiteKey ? '_blank' : null;
  if (
    contentType &&
    (contentType.toLowerCase().includes('equipment') ||
      contentType.toLowerCase().includes('taste'))
  ) {
    const winnerData = reviewSet.getDocData(item, { siteKey, tag });
    const { price } = winnerData;
    ctaUrl = winnerData.ctaUrl;

    const winnerLink = buyNowLinks[domainSiteKey]?.split('?')[0];
    const { isAmazon, ctaUrl: generatedUrl } = generateAffiliatesUrl(
      winnerLink,
      tag,
    );
    if (generatedUrl) {
      ctaUrl = generatedUrl;
    }

    ctaText = 'Buy the Winner';

    const asin = winnerData.asin || tryAsinFromCtaUrl(ctaUrl) || '';

    // TODO: after migration in cortado updating index we should have retailer
    // and recommendation status
    ctaDataAttrs = {
      'data-asin': asin,
      'data-price': price,
      'data-reviewable': title,
      'data-affiliate-tag': isAmazon ? tag : '',
      'data-affiliate-url': generatedUrl ?? ctaUrl,
    };
  }

  let shopPrices;
  if (price && salePrice) {
    shopPrices = { price, salePrice };
  }

  const dataAttrs = {
    'data-title': title,
    'data-type': contentType,
    'data-id': slug,
    'data-link': href,
  };

  const displayRecipeAttribution = getDisplayRecipeAttributions(
    isAuthenticated,
    avgRating,
    numRatings,
  );

  const val = {
    avgRating,
    contentType,
    contentTypeFormatted,
    ctaDataAttrs,
    ctaText,
    ctaUrl,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton,
    displaySecondaryAttribution,
    displayRecipeAttribution,
    favoriteRibbonColor: utils.siteKeyToRibbonColor(siteKey),
    href,
    imageUrl,
    numRatings,
    objectId: favoritableId,
    searchAttribution: true,
    searchComments: searchAttribution && commentsCount,
    shopPrices,
    siteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers,
    target,
    title: utils.truncateString(title, 59),
  };

  return val;
};

const productCollectionToStandardCard = (item) => {
  const {
    fields: {
      sourcekey,
      product: {
        fields: { category, title, photo, price, salePrice, shopid, url },
      },
    },
  } = item;

  const imageUrl = photo?.public_id
    ? getImageUrl(photo.public_id, 'thumbnail', { aspectRatio: '1:1' })
    : null;
  const shopPrices = { price, salePrice };
  const discount = Math.ceil(((price - salePrice) / price) * 100);
  const ctaUrl = `${url}${sourcekey ? `?sourcekey=${sourcekey}` : ''}`;

  return {
    contentType: category,
    ctaText: `Buy Now & Save ${discount}%`,
    ctaUrl,
    imageUrl,
    href: ctaUrl,
    objectId: shopid,
    shopPrices,
    siteKey: 'shop',
    target: '_blank',
    title,
  };
};

const sectionItemToFeatureCard = (
  item,
  user,
  domainSiteKey,
  subdomain,
  tag,
) => {
  let doc = {};
  let itemDocument = item.document;
  if (itemDocument) {
    if (itemDocument.docType === 'barista_article') {
      const { documentId } = item;
      const { slug } = itemDocument;
      itemDocument = {
        ...itemDocument,
        docType: 'article',
        id: documentId,
        slug: slug.includes(item.documentId) ? slug : `${documentId}-${slug}`,
      };
    }
    doc = documentToFeatureCard(
      itemDocument,
      user,
      domainSiteKey,
      subdomain,
      tag,
    );
  }

  const title = item.title || doc.title;
  const href = item.url || doc.href;

  // title and href fallbacks extended to data attributes when not using winner attribute logic
  if (doc.dataAttrs && !item?.document?.winner) {
    doc.dataAttrs['data-title'] = title;
    doc.dataAttrs['data-link'] = href;
  }

  return {
    ...doc,
    attributions: doc.attributions?.split(' • ')?.shift(),
    title,
    href,
  };
};

const sectionItemToReviewableCard = (item, user, siteKey, subdomain, tag) => {
  const {
    asin,
    affiliates,
    docType,
    documentType,
    name,
    photo: { publicId: cloudinaryId } = {},
    price,
    recommendationStatus,
    reviewSet: {
      documentType: reviewSetType = 'equipment_review',
      id,
      slug,
      title,
    } = {},
    winner,
    winnerHeader,
  } = item.document || {};

  const {
    photo: { public_id: cloudinaryIdOverride },
  } = item;

  let buyNowLink;
  let affiliateTag;
  let retailer;
  if (affiliates) {
    const currentAffiliate = affiliates[siteKey];
    const { name, url } = currentAffiliate;
    if (url && name === 'Amazon') {
      buyNowLink = `${url}?tag=atkequipland-20`;
      affiliateTag = 'tag=atkequipland-20';
      retailer = name;
    } else {
      buyNowLink = url ?? null;
    }
  }
  if (asin) {
    buyNowLink = `https://www.amazon.com/dp/${asin}/?tag=${
      tag || `${siteKey}prodcoll2-20`
    }`;
    affiliateTag = tag || `${siteKey}prodcoll2-20`;
    retailer = 'Amazon';
  }
  let otherAffiliateName;
  if (!asin && affiliates) {
    otherAffiliateName = affiliates[siteKey]?.name;
  }

  const docSlug = slug?.startsWith(id) ? slug : `${id}-${slug}`;
  const href = utils.getDocumentUrl(reviewSetType, docSlug, siteKey, subdomain);

  if (
    buyNowLink &&
    href &&
    (buyNowLink.includes('amazon.com') ||
      buyNowLink.includes('clicks.trx-hub.com'))
  ) {
    buyNowLink = trackonomicsLinkBuilder({
      affiliateLink: buyNowLink,
      affiliateTag,
      contentPageUrl: href,
    });
  }

  const type = documentType || docType;
  const hrefDataAttrs = {
    'data-asin': asin,
    'data-price': price,
    'data-recommendation-status': recommendationStatus,
    'data-type': type,
    'data-reviewable': name,
    'data-retailer': retailer,
    'data-review-set': title,
    'data-affiliate-tag': affiliateTag,
    'data-affiliate-url': buyNowLink,
  };

  return {
    asin,
    buyNowLink,
    buyNowOverrideAffiliateActive: true,
    buyNowOverrideAffiliateName: otherAffiliateName,
    cloudinaryId: cloudinaryIdOverride || cloudinaryId,
    href,
    hrefDataAttrs,
    name: item.title || name,
    price,
    recommendationStatus,
    winner,
    winnerHeader,
  };
};

function userAttributionsFromRelated(item) {
  return {
    commentsCount: item?.fields?.commentsCount ?? null,
    numRatings: item?.fields?.rating?.attributes?.userRatingsCount ?? null,
    avgRating: item?.fields?.rating?.attributes?.avgScore ?? null,
  };
}

const reviewRelatedToFeatureCard = ({
  item,
  domainSiteKey,
  subdomain,
  isAuthenticated,
}) => {
  const {
    fields: {
      documentType: resourceType,
      metaData,
      productReference,
      searchStickers,
      slug,
      title,
    },
  } = item;

  const hasAuth = isAuthenticated ? [true] : [];

  const userData = {
    activeRegistrations: hasAuth,
  };

  const resourceTypeFormatted =
    {
      equipment_review: 'Equipment Review',
      recipe: 'Recipe',
      taste_test: 'Taste Test',
    }[resourceType] || resourceType;
  const itemData = {
    metaData: { ...metaData.fields },
    resourceType,
    resourceTypeFormatted,
    searchStickers,
    slug,
    title,
    winner: { ...productReference?.fields },
    ...userAttributionsFromRelated(item),
  };

  return relatedToFeatureCard({
    item: itemData,
    userData,
    domainSiteKey,
    subdomain,
  });
};

const sectionItemToStandardCard = (item, ...args) => {
  const {
    document,
    documentType,
    photo: { public_id: publicId } = {},
    title,
    url,
  } = item;

  let resourceTypeFormatted = document?.resourceTypeFormatted;
  if (['Equipment Review', 'Taste Test'].includes(resourceTypeFormatted)) {
    resourceTypeFormatted = 'Review';
  }
  let landscapePhoto;
  if (documentType === 'Cms::Page::Article') {
    resourceTypeFormatted = 'Article';
    landscapePhoto = document?.landscapeHeroPhoto?.publicId;
  }

  const photo = document?.metaData?.photo ?? { publicId };
  if (!photo?.publicId) {
    photo.publicId = landscapePhoto;
  }

  const siteList = document?.metaData?.siteList;
  const itemData = {
    ...document,
    rating: document?.rating,
    metaData: {
      photo,
      title: title || document.title,
      siteList,
    },
    resourceType: documentType,
    resourceTypeFormatted,
    sourceKey:
      documentType === 'RetailProduct' ? `${args[args.length - 1]}` : '',
    title: title || document?.title,
    url: url || document?.url,
  };

  return itemToStandardCard(itemData, ...args);
};

const sectionItemToCastMember = ({
  bio: description,
  firstName,
  lastName,
  photo: { publicId: imgCloudinaryId } = {},
}) => ({
  description,
  name: `${firstName} ${lastName}`,
  imgCloudinaryId,
  mode: 'light',
});

const buyingGuideSectionItemToReviewableCard = (
  item,
  user,
  siteKey,
  subdomain,
  tag,
) => {
  const { fields: { documentReference = {}, productReference = {} } = {} } =
    item || {};
  const options = {
    displayPrice: false,
    siteKey,
    tag,
    buyingGuide: true,
  };
  const {
    asin,
    ctaUrl: buyNowLink,
    photo: { id: cloudinaryId } = {},
    price,
    title: name,
  } = reviewSet.getProductData(productReference, options);

  const { documentType, slug, title } = documentReference;
  const { recommendationStatus } = productReference;

  const href = utils.getDocumentUrl(documentType, slug, siteKey, subdomain);

  const hrefDataAttrs = {
    'data-type': documentType,
    'data-title': title,
  };

  return {
    asin,
    buyNowLink,
    buyNowOverrideAffiliateActive: true,
    cloudinaryId,
    href,
    hrefDataAttrs,
    name,
    price,
    recommendationStatus,
  };
};

const itemToSmallRelatedCard = (
  item,
  user,
  domainSiteKey,
  subdomain = 'www',
) => {
  const siteKey = utils.getDocSiteKey(item, domainSiteKey);
  const href = utils.getDocumentUrl(
    item.resourceType,
    item.slug,
    siteKey,
    subdomain,
  );

  const cloudinaryId =
    item.metaData?.photo?.publicId || item.metaData?.photo?.public_id;
  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'thumbnail', { aspectRatio: '1:1' })
    : null;
  const target = siteKey !== domainSiteKey ? '_blank' : null;

  const dataAttrs = {
    'data-title': item.title,
    'data-type': item.resourceType,
    'data-id': item.slug,
    'data-link': href,
  };

  return {
    dataAttrs,
    href,
    imageUrl,
    target,
    title: utils.truncateString(item.title, 59),
  };
};

const suggestionToStandardCard = (item, user, siteKey, subdomain) => {
  const {
    commentsCount,
    metaData = {},
    resourceType: contentType,
    resourceTypeFormatted: contentTypeFormatted,
    slug,
    title,
  } = item;

  const { photo: { publicId: cloudinaryId } = {} } = metaData;
  const isAuthenticated = getIsAuthenticated(user);

  const { favoritableId, favoritableSiteKey } =
    utils.getFavoritableDataFromDocument(
      { documentType: contentType, metaData, fields: { slug } },
      siteKey,
      subdomain,
      isAuthenticated,
    );
  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'searchResultImage', 'grid')
    : null;

  // full url takes into account subdomain and site key
  const href = utils.getDocumentUrl(
    contentType,
    slug,
    favoritableSiteKey,
    subdomain,
  );

  const dataAttrs = {
    'data-title': title,
    'data-type': contentType,
    'data-id': slug,
    'data-link': href,
  };

  const transformedItem = {
    contentType,
    contentTypeFormatted,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton: false,
    displaySecondaryAttribution: true,
    href,
    imageUrl,
    objectId: favoritableId,
    secondaryAttribution: `${commentsCount} comments`,
    siteKey: favoritableSiteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers: [],
    title: utils.truncateString(title, 52),
  };
  return transformedItem;
};

export const normalizeResourceType = ({
  resourceType,
  resourceTypeFormatted,
}) => {
  if (!resourceType) return { resourceType, resourceTypeFormatted };
  if (resourceType in utils.resourceTypeTranslation) {
    resourceType = utils.resourceTypeTranslation[resourceType];
  }
  resourceTypeFormatted = utils.docTypeFormatted[resourceType];
  return { resourceType, resourceTypeFormatted };
};

export const relatedToStandardCard = (
  item,
  isAuthenticated,
  domainSiteKey,
  subdomain,
) => {
  const { fields } = item;
  const { document, id, url, title, photo } = fields || {};

  let { resourceType, resourceTypeFormatted } = fields || {};
  let siteKey = domainSiteKey;
  let searchComments;
  let searchRatings;
  let stickers = [];
  let href = url;
  let ctaText = null;
  let ctaUrl = null;
  let ctaDataAttrs = null;
  let target = null;
  if (subdomain && url?.includes('www.')) {
    href = url.replace('www.', `${subdomain}.`);
  }

  const { public_id: cloudinaryId } = photo || {};
  const imageUrl = cloudinaryId
    ? getImageUrl(cloudinaryId, 'searchResultImage', 'grid')
    : null;

  if (!resourceType) {
    const { docType } = utils.getDocInfoFromUrl(url) || {};
    resourceType = utils.collectionTypeToDocType[docType];
  }
  ({ resourceType, resourceTypeFormatted } = normalizeResourceType({
    resourceType,
    resourceTypeFormatted,
  }));

  const toFloat = (value) => {
    const result = Number((Math.round(value * 10) / 10).toFixed(1));
    if (Number.isInteger(result) && result > 0) {
      return Number(`${result}.0`);
    }
    return result;
  };

  if (document) {
    const { documentType, fields, publishDate } = document;
    const {
      commentsCount,
      price,
      salePrice,
      searchStickers,
      slug,
      sourceKey,
      title,
      winner,
      rating,
    } = fields || {};
    searchComments = commentsCount > 0 ? commentsCount : null;

    searchRatings = rating
      ? {
          ...rating,
          attributes: {
            ...rating.attributes,
            avgScore: toFloat(rating.attributes.avgScore),
          },
        }
      : { attributes: { avgScore: null, userRatingsCount: null } };

    siteKey = utils.getDocSiteKey(document, domainSiteKey);
    target = siteKey !== domainSiteKey ? '_blank' : null;

    ({ resourceType, resourceTypeFormatted } = document);
    ({ resourceType, resourceTypeFormatted } = normalizeResourceType({
      resourceType,
      resourceTypeFormatted,
    }));

    href = utils.getDocumentUrl(resourceType, slug, siteKey, subdomain);
    if (publishDate && searchStickers) {
      stickers = utils.getStickers(publishDate, searchStickers);
    }
    if (
      resourceType &&
      (resourceType.toLowerCase().includes('equipment') ||
        resourceType.toLowerCase().includes('taste'))
    ) {
      const winnerData = reviewSet.getDocData(
        { documentType, slug, title, winner },
        { siteKey },
      );
      const { asin, price } = winnerData;
      let amazonLink;
      if (winnerData && winnerData?.ctaUrl) {
        amazonLink = winnerData?.ctaUrl?.split('?tag')[0];
        ctaUrl = `${amazonLink}?tag=atkstandardcard-20`;
      }
      ctaText = 'Buy the Winner';

      ctaDataAttrs = {
        'data-asin': asin,
        'data-price': price,
        'data-reviewable': title,
        'data-affiliate-tag': 'atkstandardcard-20',
        'data-affiliate-url': ctaUrl,
      };
    } else if (resourceType === 'RetailProduct') {
      const hasKey = url.includes(sourceKey);
      const joiner = url.includes('?') ? '&' : '?';
      ctaUrl = `${url}${
        sourceKey && !hasKey ? `${joiner}sourcekey=${sourceKey}` : ''
      }`;
      ctaText =
        price && salePrice
          ? `Buy Now & Save ${Math.abs(
              Math.round(Math.min(salePrice / price) * 100) - 100,
            )}%`
          : 'Buy Now';
      href = ctaUrl;
      target = '_blank';
    }
  }

  const { favoritableId, favoritableSiteKey } = utils.getFavoritableDataFromUrl(
    href,
    siteKey,
  );

  const displayFavoritesButton = isAuthenticated && Boolean(favoritableId);
  const dataAttrs = {
    'data-title': title,
    'data-type': resourceType,
    'data-id': id,
    'data-link': url,
  };
  const transformedItem = {
    contentType: resourceType,
    contentTypeFormatted: resourceTypeFormatted,
    ctaDataAttrs,
    ctaText,
    ctaUrl,
    dataAttrs,
    displayLockIcon: false,
    displayFavoritesButton,
    isAuthenticated,
    href,
    imageUrl,
    objectId: favoritableId,
    searchComments,
    searchRatings,
    siteKey: favoritableSiteKey,
    siteKeyFavorites: favoritableSiteKey,
    stickers,
    target,
    title: utils.truncateString(title, 52),
  };

  return transformedItem;
};

/**
 * Check for html with empty content like "<br>" that sometimes comes from barista
 *  if seeing unexpected description value selected.
 */
function getIndexHeroDescription(lead) {
  const description =
    lead?.document?.dekLines || // articles fallback
    lead?.document?.dek || // reviews fallback
    lead?.document?.description || // clips fallback
    lead?.document?.shortDescription || // buying_guide fallback
    lead?.description || // manual barista entry
    lead?.subtitle || // manual barista entry using another key.
    lead?.metaData?.description; // older item fallback

  /*
   * descriptions render in dangerouslySetInnerHTML, some descriptions are wrapped in
   *  paragraph tags and nested paragraph tags error out so we remove them here.
   */
  return striptags(description, ['a', 'strong', 'em']);
}

function getIndexHeroHref(lead, siteKey, subdomain) {
  const { documentId, document } = lead;
  const { docType, slug } = document;

  const docSiteKey = getDocSiteKey(lead?.document, siteKey);

  /** Some document.slug values are missing the documentId at the beginning */
  // EDCs dont use their ids in their slugs
  const slugHasDocumentId = slug.startsWith(documentId);

  // EDC slugs do not use their id, so bypass passing their document id
  // to the helper function
  return slugHasDocumentId ||
    document.docType === 'editorial_document_collection'
    ? utils.getDocumentUrl(docType, slug, docSiteKey, subdomain)
    : utils.getDocumentUrl(
        docType,
        `${documentId}-${slug}`,
        docSiteKey,
        subdomain,
      );
}

function getIndexHeroImage(lead) {
  const { document, photo } = lead;
  return (
    photo?.public_id ?? // manual entry and reviews landscape photo
    document?.landscapeHeroPhoto?.publicId ?? // barista articles 1
    document?.photo?.publicId ?? // barista articles 2
    document?.metaData?.meta_image ?? // images in older documents
    document?.metaData?.photo?.publicId
  ); // reviews portrait image
}

function getDocumentId(lead) {
  return (
    lead?.document?.id ?? // most documents
    lead?.documentId
  ); // barista_articles
}

const parseLeadMarquee = (leadData, siteKey = 'atk', subdomain = 'www') => {
  const { document } = leadData;
  const docSiteKey = getDocSiteKey(document, siteKey);

  const description = getIndexHeroDescription(leadData);
  const href = getIndexHeroHref(leadData, docSiteKey, subdomain);
  const cloudinaryId = getIndexHeroImage(leadData);
  const authorProps = { authors: leadData.authors };
  const docId = getDocumentId(leadData);

  let parsedResponse = {};
  switch (leadData?.documentType) {
    case 'ReviewSet::EquipmentReview':
    case 'ReviewSet::TasteTest':
      const {
        document: { commentsCount, resourceType, slug: reviewSlug, title },
        hexColor,
        label,
        photo,
      } = leadData;

      parsedResponse = {
        bgColor: hexColor || '321A52',
        commentsCount,
        description,
        docId,
        docSiteKey,
        docType: resourceType,
        imageUrl: photo?.public_id,
        label,
        slug: reviewSlug,
        title,
        href,
        cloudinaryId,
        authorProps,
      };
      break;
    default:
      const {
        document = {},
        hexColor: hex,
        label: docLabel,
        title: docTitle,
      } = leadData;
      const {
        commentsCount: comments,
        docType,
        metaData = {},
        rating,
        slug,
      } = document;
      const { meta_image: img } = metaData;
      const {
        attributes: { avgScore: avgRating, userRatingsCount: numRatings } = {},
      } = rating ?? {};
      parsedResponse = {
        avgRating,
        numRatings,
        commentsCount: comments,
        docId,
        bgColor: hex || '321A52',
        description,
        docType,
        docSiteKey,
        imageUrl: img,
        label: docLabel,
        slug,
        title: docTitle,
        href,
        cloudinaryId,
        authorProps,
      };
  }

  return parsedResponse;
};

const transforms = {
  algoliaHitToStandardCard,
  buyingGuideSectionItemToReviewableCard,
  documentToGenericCard,
  documentToDocumentBrowseCard,
  documentToFeatureCard,
  genericCardToDocumentBrowseCard,
  itemToFeatureCard,
  itemToPersonCard,
  itemToStandardCard,
  parseLeadMarquee,
  productCollectionToStandardCard,
  relatedToStandardCard,
  reviewRelatedToFeatureCard,
  sectionItemToFeatureCard,
  sectionItemToReviewableCard,
  sectionItemToStandardCard,
  sectionItemToCastMember,
  suggestionToStandardCard,
  trendingToDocumentBrowseCard,
  trendingToStandardCard,
  itemToSmallRelatedCard,
};

export default transforms;
